/**
 * Een potje dat op de server draait.
 *
 * De tweelingbroer van `lokaalspel.ts`, en dat is het hele punt van fase 0: het scherm
 * praat met dezelfde vorm — vraag een weergave, stuur een zet, krijg een antwoord dat ook
 * "nee" kan zijn — en merkt niet of de stand hiernaast in het geheugen ligt of duizend
 * kilometer verderop in een database.
 *
 * ## Het netwerk is geen randgeval
 *
 * De eerste versie van dit bestand ging ervan uit dat fetch slaagt. Eén hikje — wifi even
 * weg, een 502 van de proxy, een tabblad dat geschorst werd — en de pollus stierf stil:
 * het scherm bevroor op de laatste stand en er kwam nooit meer iets binnen. Daarom geldt
 * hier nu één wet: **geen enkele netwerkuitkomst mag de lus doden.** Een fout is een
 * uitkomst, geen uitzondering. De lus loopt door met een oplopende wachttijd, meldt de
 * verbindingstoestand aan het scherm, en herstelt vanzelf zodra het weer kan.
 *
 * Daarom is `pasAan` hier niet geïmplementeerd. De toetshaak kan er dus niet bij, en dat
 * hoort ook zo: de stand is niet van deze browser.
 */
import { NaarClientSchema, type NaarClient } from '@catan/contracts';
import type { Action, GameView } from '@catan/rules';
import type { Zichtbarezet } from './afspelen.js';
import type { Computerzet, Potje, Zetuitkomst } from './lokaalspel.js';

/** Wat de server terugstuurt na een zet of bij het ophalen. */
interface Weergavebericht {
  readonly seq: number;
  readonly log: readonly string[];
  readonly view: GameView;
  readonly figuur?: string | null | undefined;
  readonly jouwStoel?: number | undefined;
  readonly tegenComputer?: boolean | undefined;
  readonly anderWeg?: boolean | undefined;
  readonly status?: string | undefined;
  /**
   * De zetten sinds `sinds`, alleen aanwezig als we erom vroegen.
   *
   * Nooit als aanwezig aannemen: tegen een oudere server komen ze niet mee, en dan hoort
   * het scherm zich gewoon te gedragen als voorheen.
   */
  readonly zetten?: readonly Zichtbarezet[] | undefined;
  /** Het figuur van de tegenstander, zodat hij niet als Louis in beeld komt. */
  readonly tegenfiguur?: string | null | undefined;
}

interface Fout {
  readonly fout: string;
  readonly bericht: string;
}

function leesBericht(waarde: unknown): NaarClient | undefined {
  const uit = NaarClientSchema.safeParse(waarde);
  return uit.success ? uit.data : undefined;
}

/**
 * Roept de server aan. Geeft áltijd een waarde terug, nooit een uitzondering.
 *
 * `fout: 'NETWERK'` betekent: de vraag is misschien niet eens aangekomen. Dat is wezenlijk
 * anders dan een nee van de server, en de pollus behandelt het ook anders (opnieuw
 * proberen in plaats van stoppen).
 */
async function roep(
  pad: string,
  lichaam?: unknown,
  signal?: AbortSignal,
): Promise<Weergavebericht | Fout> {
  let res: Response;
  try {
    res = await fetch(pad, {
      method: lichaam === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      ...(signal === undefined ? {} : { signal }),
      ...(lichaam === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(lichaam) }),
    });
  } catch {
    // Ook een afgebroken poll komt hier terecht. Dat is goed: de aanroeper kijkt toch
    // eerst naar `gesloten` en start dan niets opnieuw.
    return { fout: 'NETWERK', bericht: 'geen verbinding met de server' };
  }
  const ruw: unknown = await res.json().catch(() => undefined);
  const bericht = leesBericht(ruw);

  if (bericht === undefined) {
    // Een antwoord dat niet door het schema komt: een proxyfoutpagina, een oude
    // serverversie. Behandelen als hapering, niet als oordeel.
    return { fout: 'NETWERK', bericht: `de server gaf ${res.status}` };
  }
  if (bericht.type === 'fout') return { fout: bericht.fout, bericht: bericht.bericht };
  return {
    seq: bericht.seq,
    log: bericht.log,
    view: bericht.view as GameView,
    figuur: bericht.figuur,
    jouwStoel: bericht.jouwStoel,
    tegenComputer: bericht.tegenComputer,
    anderWeg: bericht.anderWeg,
    status: bericht.status,
    zetten: bericht.zetten as readonly Zichtbarezet[] | undefined,
    tegenfiguur: bericht.tegenfiguur,
  };
}

/** Een sleutel die één zet één keer laat tellen, ook na opnieuw versturen. */
const nieuweSleutel = (): string => crypto.randomUUID().replace(/-/g, '');

/** Hoe het er met de verbinding voorstaat, voor een indicator op het scherm. */
export type Verbinding = 'ok' | 'haperend' | 'weg';

/** Na hoeveel mislukte pogingen we "haperend" tegen "weg" inruilen. */
const WEG_NA = 3;
/**
 * Het poll-tempo terwijl je op je tegenstander wacht.
 *
 * Eén seconde, en dat is precies de plek waar het merkbaar is: dit is de wachttijd tussen
 * zijn zet en het moment dat jij zijn worp ziet rollen. Twee seconden voelde als een
 * hapering in het spel zelf.
 *
 * Sneller heeft weinig zin: de animatie erna duurt seconden, en elke poll is een verzoek
 * dat de server verwerkt. Wanneer jíj aan zet bent tikt hij op `HARTSLAG_MS` — daar valt
 * niets op te halen, en het enige doel is je aanwezig melden.
 */
const POLL_MS = 1000;
/** Het plafond voor de oplopende wachttijd bij haperingen. */
const MAX_WACHT_MS = 30_000;
/** Hoe vaak we ons melden als er niets te vragen valt (jij bent aan zet). */
const HARTSLAG_MS = 30_000;

export interface NetPotjeOpties {
  readonly gameId: string;
  /** Het figuur dat jij speelt; het scherm gebruikt het voor je portret. */
  readonly figuur: string;
  /**
   * Aangeroepen zodra er buiten jou om iets veranderd is: een zet van de ander, een
   * verlopen aanbod, een potje dat afgelopen blijkt, of een verbinding die wegvalt of
   * terugkomt.
   *
   * Krijgt de zetten mee die sinds de vorige melding binnenkwamen, zodat het scherm de
   * beurt van de ander kan afspelen. Vaak leeg — bij een verbindingswissel of een verlopen
   * aanbod is er niets te zien.
   */
  readonly onNieuweStand?: ((zetten: readonly Zichtbarezet[], gat: boolean) => void) | undefined;
  /**
   * De sessie is weg: de server kent dit koekje niet meer. Eén keer aangeroepen; daarna
   * stopt dit potje met vragen. Zonder deze melding bleef het scherm "Ingelogd als …"
   * tonen terwijl elke zet strandde op een onbegrijpelijke fout.
   */
  readonly onSessieWeg?: (() => void) | undefined;
}

/**
 * Opent een potje dat op de server draait.
 *
 * Haalt meteen de stand op, want zonder weergave kan het scherm niets tekenen. Alleen dít
 * eerste ophalen mag falen met een uitzondering: zonder beginstand ís er geen potje.
 */
export async function netPotje(opties: NetPotjeOpties): Promise<Potje> {
  // `sinds=` is méér dan een filter: de aanwezigheid ervan vertelt de server dat deze
  // bundel de nieuwe velden aankan. Een oudere bundel stuurt hem niet en krijgt het
  // antwoord van vroeger — zo kan een tabblad dat een uitrol overleeft niet stuklopen op
  // een veld dat het niet kent. Een functie en geen constante, want `seq` schuift op.
  const pad = (): string =>
    `/api/potje?id=${encodeURIComponent(opties.gameId)}&sinds=${gespeeldTot}`;
  let gespeeldTot = 0;
  // Ook het openen valt onder de wet van dit bestand: een hapering is geen oordeel. Drie
  // pogingen voordat we het opgeven — een potje dat net gestart is, is het potje waar
  // beide spelers op zitten te wachten, en "kon niet geopend worden" liet je stilletjes
  // in een lokaal potje achter terwijl je tegenstander in het echte zat.
  let eerste = await roep(pad());
  for (let poging = 0; 'fout' in eerste && eerste.fout === 'NETWERK' && poging < 3; poging++) {
    await new Promise((klaar) => window.setTimeout(klaar, 800 * (poging + 1)));
    eerste = await roep(pad());
  }
  if ('fout' in eerste) throw new Error(`potje openen mislukte: ${eerste.bericht}`);

  let view = eerste.view;
  let seq = eerste.seq;
  // De zetten die bij het openen meekwamen gooien we weg: je hervat een potje, je wilt niet
  // eerst de laatste twintig zetten van je eigen geschiedenis terugzien.
  gespeeldTot = eerste.seq;
  const tegenComputer = eerste.tegenComputer ?? false;
  const figuur = eerste.figuur ?? opties.figuur;
  let tegenfiguur = eerste.tegenfiguur ?? undefined;
  let status = eerste.status ?? 'active';
  let anderWeg = eerste.anderWeg ?? false;
  let verbinding: Verbinding = 'ok';
  let mislukt = 0;
  let gesloten = false;
  const log: string[] = [...eerste.log];

  let teSpelen: Zichtbarezet[] = [];
  let gatGezien = false;

  const meldNieuw = (): void => {
    if (gesloten) return;
    const zetten = teSpelen;
    const gat = gatGezien;
    teSpelen = [];
    gatGezien = false;
    opties.onNieuweStand?.(zetten, gat);
  };

  /** Verwerkt een binnengekomen weergave. Negeert wat ouder is dan wat we al hebben. */
  const neemOver = (uit: Weergavebericht, volledigeLog: boolean): boolean => {
    // Twee antwoorden kunnen elkaar kruisen; de laatst aangekomene hoeft niet de nieuwste
    // te zijn. Alles wat terugwijst in de tijd wordt genegeerd.
    if (uit.seq < seq) return false;
    const veranderd =
      uit.seq !== seq ||
      (uit.anderWeg ?? anderWeg) !== anderWeg ||
      (uit.status ?? status) !== status;

    // Boekhouden op **seq**, niet op stoel. Een `ruil-antwoord` dat de server namens een
    // niet-antwoordende speler wegschrijft staat op díéns naam — en dat kan jij zijn. Een
    // stoel-filter gooit dan precies de zet weg die verklaart waarom je aanbod verdween.
    const nieuw = (uit.zetten ?? []).filter((z) => z.seq > gespeeldTot);
    if (nieuw.length > 0) teSpelen.push(...(nieuw as Zichtbarezet[]));
    // Meer opgeschoven dan we zetten kregen? Dan misten we er een — door een momentopname
    // aan de serverkant of een poll die niet aankwam. Liever een schone sprong.
    if (uit.seq - gespeeldTot > nieuw.length) gatGezien = true;
    gespeeldTot = Math.max(gespeeldTot, uit.seq);

    view = uit.view;
    seq = uit.seq;
    status = uit.status ?? status;
    anderWeg = uit.anderWeg ?? anderWeg;
    if (uit.tegenfiguur !== undefined && uit.tegenfiguur !== null) tegenfiguur = uit.tegenfiguur;
    if (veranderd && volledigeLog) {
      log.length = 0;
      log.push(...uit.log);
    }
    return veranderd;
  };

  /**
   * Ben jij degene die nu iets moet doen?
   *
   * Dezelfde afweging als `wieMoet` op de server, en met opzet niet alleen `yourTurn`: heb
   * jij net een ruilaanbod gedaan, dan is het nog steeds jouw beurt maar ligt de bal bij de
   * ander. En in de opzet is er helemaal geen beurt.
   */
  const ikMoet = (): boolean => {
    if (view.pending !== undefined) return view.pending.seat === view.you.seat;
    if (view.offer !== undefined) return view.offer.from !== view.you.seat;
    if (view.phase === 'opzet') return false;
    return view.yourTurn;
  };

  /**
   * De pollus. Loopt zolang het potje open en actief is; niets mag hem doden.
   *
   * Drie snelheden: het gewone tempo als we op de ander wachten, een oplopende wachttijd
   * als het netwerk hapert, en de trage hartslag als jij aan zet bent — die laatste is er
   * niet om iets op te halen maar om je aanwezig te melden. Zonder hartslag stond je na
   * drie minuten nadenken als "weggelopen" te boek en mocht je tegenstander het potje
   * opgeven terwijl jij naar het bord zat te kijken.
   */
  let timer: number | undefined;
  /** De poll die nú onderweg is, om bij het sluiten af te kunnen breken. */
  let lopendePoll: AbortController | undefined;
  const polsen = (): void => {
    if (gesloten) return;
    if (timer !== undefined) window.clearTimeout(timer);
    if (status !== 'active') return;
    // Tegen de computer valt er normaal niets te vragen: zijn beurt zit al in het
    // antwoord op de jouwe. Behalve in één abnormale toestand — de computer bleef aan
    // zet staan. Dan tikt er hier een trage poll, en de server speelt hem bij het
    // ophalen alsnog uit. Is het gewoon jouw beurt, dan is er niets te doen.
    if (tegenComputer && ikMoet()) return;

    const wacht =
      mislukt > 0
        ? Math.min(POLL_MS * 2 ** mislukt, MAX_WACHT_MS)
        : ikMoet()
          ? HARTSLAG_MS
          : tegenComputer
            ? 3000
            : POLL_MS;

    timer = window.setTimeout(() => {
      void (async () => {
        // Met een afbreker, zodat `sluit()` een poll die nog onderweg is echt afkapt in
        // plaats van hem te laten bungelen tot de browser hem opruimt. Alleen de GET:
        // een zet-POST breek je nooit af, want de server kan hem al verwerkt hebben.
        lopendePoll = new AbortController();
        const uit = await roep(pad(), undefined, lopendePoll.signal);
        lopendePoll = undefined;
        if (gesloten) return;

        try {
          if ('fout' in uit) {
            if (uit.fout === 'NETWERK') {
              // Hapering: doorgaan, trager, en het scherm laten zien dat er iets is.
              mislukt += 1;
              const nieuw: Verbinding = mislukt >= WEG_NA ? 'weg' : 'haperend';
              if (nieuw !== verbinding) {
                verbinding = nieuw;
                meldNieuw();
              }
            } else if (uit.fout === 'NIET_INGELOGD') {
              gesloten = true;
              opties.onSessieWeg?.();
              return;
            } else {
              // Een oordeel van de server: dit potje valt niet meer te volgen. Stoppen met
              // vragen, maar wél doorgeven — een stil einde is precies wat we niet willen.
              status = uit.fout === 'AL_AFGELOPEN' ? 'abandoned' : 'onbereikbaar';
              meldNieuw();
              return;
            }
          } else {
            const hersteld = verbinding !== 'ok';
            verbinding = 'ok';
            mislukt = 0;
            const veranderd = neemOver(uit, true);
            // Zolang de ander weg is, elke poll melden — ook zonder verandering. Het scherm
            // tekent alleen bij een melding, en de opgeefvraag bestaat alleen op het scherm:
            // wie al weg wás vóór de laatste tekening, bleef anders voor eeuwig onzichtbaar.
            if (veranderd || hersteld || (anderWeg && status === 'active')) meldNieuw();
          }
        } finally {
          // De volgende poll staat áltijd klaar — ook als het verwerken van dit antwoord
          // omvalt. `meldNieuw` loopt het hele scherm door, en één tekenfout daarin
          // doodde anders stilletjes de complete pollus: geen stand meer, geen melding,
          // een potje dat gewoon ophoudt te bewegen. De twee stop-gevallen hierboven
          // bewaken zichzelf: `polsen` weigert bij `gesloten` en bij een gestrande status.
          polsen();
        }
      })();
    }, wacht);
  };
  polsen();

  return {
    seed: opties.gameId,
    opties: {
      seed: opties.gameId,
      tegenstander: view.opponent.name,
      moeilijkheid: '',
      zelfVerdelen: false,
    },

    weergave: () => view,
    verslag: () => log,
    meld: (regel) => log.unshift(regel),

    async zet(actie: Action): Promise<Zetuitkomst> {
      // `zetten=1` in de zoekreeks en niet in het lichaam: `ZetSchema` is `.strict()`, dus
      // een extra veld in het lichaam naar een oudere server zou de zet laten weigeren. Een
      // onbekende queryparameter negeert zo'n server gewoon.
      const uit = await roep(`/api/potje/zet?id=${encodeURIComponent(opties.gameId)}&zetten=1`, {
        type: 'zet',
        seq,
        idempotencyKey: nieuweSleutel(),
        action: actie,
      });
      if ('fout' in uit) {
        // Een geweigerde zet betekent bijna altijd dat ónze stand achterloopt — er kwam
        // iemand tussen, of we misten een poll. Meteen de echte stand halen, anders is
        // elke volgende zet óók illegaal en zit je vast tot een harde verversing.
        if (uit.fout === 'STALE_SEQ' || uit.fout === 'ILLEGAL_MOVE') {
          const vers = await roep(pad());
          if (!('fout' in vers) && neemOver(vers, true)) meldNieuw();
        } else if (uit.fout === 'NIET_INGELOGD') {
          gesloten = true;
          opties.onSessieWeg?.();
        } else if (uit.fout === 'AL_AFGELOPEN' || uit.fout === 'ONHERSPEELBAAR') {
          status = 'abandoned';
          meldNieuw();
        }
        return { ok: false, reden: uit.bericht, log: [] };
      }

      // `neemOver` schuift `gespeeldTot` op en verzamelt de zetten; die van jóu staat
      // vooraan (de server zet hem daar altijd als eerste, zie `doeZet`). Die laten we
      // vallen: `naZet` heeft hem net al laten zien.
      neemOver(uit, false);
      const nieuw = teSpelen;
      teSpelen = [];
      gatGezien = false;
      log.unshift(...[...uit.log].reverse());
      // Is het nu de ander? Dan gaan we kijken wanneer hij klaar is.
      polsen();
      return { ok: true, log: uit.log, zetten: nieuw.slice(1) };
    },

    /**
     * De computer heeft zijn zetten al gedaan voordat dit antwoord binnenkwam.
     *
     * Op de server loopt de beurt van de computer meteen achter die van jou aan; er valt
     * hier dus niets meer te zetten. Het scherm gebruikt deze twee alleen om te weten of
     * het nog moet wachten, en dat is het antwoord: nee.
     */
    computerAanZet: () => false,
    computerZet: (): Computerzet | undefined => undefined,

    anderWeg: () => anderWeg && status === 'active',

    tegenMens: () => !tegenComputer,

    /** Het figuur dat de server voor jouw stoel bewaart — juist ook in een nieuw tabblad. */
    figuur: () => figuur,

    /**
     * Het figuur van je tegenstander.
     *
     * `undefined` tegen een oudere server of in een lokaal potje; het scherm valt dan terug
     * op de naam, en anders op Louis — precies zoals voorheen.
     */
    tegenfiguur: () => tegenfiguur,

    status: () => status,

    verbinding: () => verbinding,

    async geefOp(): Promise<string | undefined> {
      let res: Response;
      try {
        res = await fetch(`/api/potje/opgeven?id=${encodeURIComponent(opties.gameId)}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: '{}',
        });
      } catch {
        return 'geen verbinding met de server';
      }
      const ruw = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (res.ok) {
        status = 'abandoned';
        return undefined;
      }
      return typeof ruw['bericht'] === 'string' ? ruw['bericht'] : 'opgeven lukte niet';
    },

    /** Stopt met vragen, voorgoed. Aanroepen zodra dit potje niet meer in beeld staat. */
    sluit() {
      // De vlag is het echte werk: een antwoord dat nú onderweg is mag de lus niet
      // herstarten. Zonder vlag bleef een verlaten potje pollen — en elke poll meldt je
      // als aanwezig, zodat je tegenstander het potje nooit meer kon opgeven.
      gesloten = true;
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      lopendePoll?.abort();
      lopendePoll = undefined;
    },
  };
}
